import React from 'react';
import { Box, Typography, Icon, Skeleton } from '@mui/material';
import { motion } from 'framer-motion';

// آیکون‌ها
import StorefrontIcon from '@mui/icons-material/Storefront';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import UndoIcon from '@mui/icons-material/Undo';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

// --- اصلاح کلیدی در کامپوننت StatCard ---
// پراپ `clickable` حذف شده است.
const StatCard = ({ title, value, previousValue, icon, loading, color, onClick }) => {
    const formattedValue = new Intl.NumberFormat('fa-IR').format(value);
    const unit = title.includes('مبلغ') || title.includes('فروش') || title.includes('هزینه') || title.includes('برگشتی') ? ' ریال' : '';
    let percentageChange = null;
    if (previousValue !== null && previousValue !== undefined) {
        if (previousValue > 0) percentageChange = ((value - previousValue) / previousValue) * 100;
        else if (value > 0) percentageChange = 100;
        else percentageChange = 0;
    }
    const ChangeIndicator = () => {
        if (percentageChange === null || loading) return null;
        const isPositive = percentageChange >= 0;
        return (<Box sx={{ display: 'flex', alignItems: 'center', color: isPositive ? '#2ecc71' : '#e74c3c', mt: 1, direction: 'ltr' }}><Typography variant="caption" sx={{ fontWeight: 'bold' }}>{`%${Math.abs(percentageChange).toFixed(1)}`}</Typography>{isPositive ? <ArrowUpwardIcon sx={{fontSize: 16, ml: 0.5}} /> : <ArrowDownwardIcon sx={{fontSize: 16, ml: 0.5}} />}</Box>);
    };

    // اکنون onClick و cursor برای همه کارت‌ها اعمال می‌شود
    return (
        <Box 
            onClick={!loading ? onClick : null} 
            sx={{ 
                p: 2, 
                height: '100%', 
                borderRadius: '12px', 
                border: '1px solid rgba(255, 255, 255, 0.2)', 
                background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.1))', 
                backdropFilter: 'blur(8px)', 
                color: '#fff', 
                display: 'flex', 
                flexDirection: 'column', 
                justifyContent: 'space-between', 
                cursor: !loading ? 'pointer' : 'default', // تغییر cursor
                transition: 'transform 0.2s, box-shadow 0.2s', 
                '&:hover': {
                    transform: !loading ? 'translateY(-5px)' : 'none', 
                    boxShadow: !loading ? '0 8px 20px rgba(0,0,0,0.3)' : 'none'
                }
            }}
        >
            {loading ? (
                <>
                    <Skeleton variant="text" width="60%" sx={{bgcolor: 'grey.700'}} />
                    <Skeleton variant="text" width="40%" height={40} sx={{bgcolor: 'grey.700', my: 1}} />
                    <Skeleton variant="circular" width={24} height={24} sx={{bgcolor: 'grey.700'}} />
                </>
            ) : (
                <>
                    <Box sx={{display:'flex', justifyContent:'space-between', alignItems:'flex-start'}}>
                        <Typography variant="body2" color="rgba(255, 255, 255, 0.7)">{title}</Typography>
                        <Icon component={icon} sx={{ color, opacity: 0.8 }} />
                    </Box>
                    <Typography variant="h5" component="p" sx={{ fontWeight: 'bold', my: 1 }}>
                        {formattedValue}
                        <Typography component="span" variant="caption" sx={{opacity: 0.7, mr: 0.5}}>{unit}</Typography>
                    </Typography>
                    <ChangeIndicator />
                </>
            )}
        </Box>
    );
};


const StatCardsGrid = ({ loading, data, onCardClick }) => {
    const statItems = [
        { title: 'فروش ناخالص', key: 'gross_sales', icon: StorefrontIcon, color: '#2ecc71' },
        { title: 'فروش خالص', key: 'net_sales', icon: AccountBalanceWalletIcon, color: '#27ae60' },
        { title: 'سفارش‌ها', key: 'orders_count', icon: ShoppingCartIcon, color: '#3498db' },
        { title: 'میانگین مبلغ سفارش', key: 'avg_order_value', icon: PointOfSaleIcon, color: '#2980b9' },
        { title: 'محصولات فروخته شده', key: 'items_sold', icon: Inventory2Icon, color: '#9b59b6' },
        { title: 'مبلغ برگشتی', key: 'refunds', icon: UndoIcon, color: '#e74c3c' },
        { title: 'مبلغ تخفیف', key: 'total_discount', icon: MoneyOffIcon, color: '#f1c40f' },
        { title: 'هزینه حمل و نقل', key: 'shipping', icon: LocalShippingIcon, color: '#e67e22' },
        { title: 'مجموع مالیات', key: 'total_tax', icon: ReceiptLongIcon, color: '#f39c12' },
        { title: 'مشتریان', key: 'total_customers', icon: PeopleAltIcon, color: '#8e44ad' },
    ];

    const containerVariants = { visible: { transition: { staggerChildren: 0.05 } } };
    const itemVariants = { hidden: { y: 20, opacity: 0 }, visible: { y: 0, opacity: 1 } };

    return (
        <Box component={motion.div} variants={containerVariants} initial="hidden" animate="visible" sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(5, 1fr)' } }}>
            {statItems.map((item) => (
                <Box component={motion.div} variants={itemVariants} key={item.key}>
                    <StatCard 
                        title={item.title} 
                        value={data?.current?.[item.key] ?? 0} 
                        previousValue={data?.previous?.[item.key]} 
                        icon={item.icon} 
                        loading={loading} 
                        color={item.color} 
                        // --- اصلاح کلیدی ---
                        // حذف پراپ clickable و اتصال مستقیم onClick
                        onClick={() => onCardClick(item)} 
                    />
                </Box>
            ))}
        </Box>
    );
};

export default StatCardsGrid;